import {
  Button,
  Chip,
  FormControlLabel,
  RadioGroup,
  TableRow,
} from "@material-ui/core";
import {
  Theme,
  ThemeOptions,
  createTheme,
  withStyles,
} from "@material-ui/core/styles";
import { Autocomplete, AutocompleteProps } from "@material-ui/lab";

declare module "@material-ui/core/styles/createBreakpoints" {
  interface BreakpointOverrides {
    xxs: true;
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    xxxl: true;
  }
}

declare module "@material-ui/core/styles/createPalette" {
  interface Palette {
    app_sand: SimplePaletteColorOptions;
    app_slate: SimplePaletteColorOptions;
  }
  interface PaletteOptions {
    app_sand?: SimplePaletteColorOptions;
    app_slate?: SimplePaletteColorOptions;
  }
}

export const baseTheme: ThemeOptions = {
  breakpoints: {
    values: {
      xxs: 0,
      xs: 480,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      xxl: 1536,
      xxxl: 1920,
    },
  },
  palette: {
    primary: {
      light: "#9bd8da",
      main: "#06A1A8", // app-teal
      dark: "#05898F",
    },
    secondary: {
      main: "#D46830", // app-orange
      dark: "#b44810",
    },
    error: {
      light: "#8a1f11",
      main: "#b22222", // app-brick
    },
    grey: {
      // app-gray
      50: "#F9FAFA",
      100: "#F3F4F5",
      200: "#EBECEE",
      300: "#DEE0E3",
      400: "#ABB0B8",
      500: "#858C98",
      600: "#6A7281",
      700: "#525C6D",
      800: "#374256",
      900: "#16233A", // app-black
      A100: "#F1F1F1",
      A200: "#CCCCCC",
      A400: "#777777",
      A700: "#515151",
    },
    common: {
      black: "#16233A", // app-black
      white: "#FFFFFE", // app-white
    },
    app_sand: {
      light: "#e7e6e4",
      main: "#dad9d7",
      dark: "#4d4b49",
    },
    app_slate: {
      main: "#003E52",
      contrastText: "#FFFFFE",
    },
  },
  typography: {
    fontFamily: "WeblySleek",
    htmlFontSize: 17,
    subtitle1: {
      fontSize: 14,
    },
    button: {
      textTransform: "none",
    },
  },
  overrides: {
    // Material has a bug where they set backgroundColor to `inherit`
    // for MuiListSubheader-sticky but backgroundColor is not an
    // inheritable property so it ends up getting the `initial`
    // value of `transparent` which causes the fonts to overlap.
    MuiListSubheader: {
      sticky: {
        backgroundColor: "#FFF",
      },
    },

    MuiFormControlLabel: {
      label: {
        fontSize: 14,
        fontWeight: "normal",
      },
    },

    MuiInputLabel: {
      outlined: {
        transform: "translate(14px, 15px) scale(1)",
      },
    },

    // We still use Bootstrap input controls pretty commonly. This styles
    // the material input to closer match bootstrap's style when using the
    // outline variant
    MuiOutlinedInput: {
      root: {
        "&$focused $notchedOutline": {
          borderColor: "#83d0d4",
          boxShadow:
            "inset 0 1px 1px rgba(0,62,82,.075), 0 0 8px rgba(6,161,168,.6)",
          borderWidth: "1px",
        },
        "&:hover $notchedOutline": {
          borderColor: "#ccc",
        },
      },
      focused: {},
      notchedOutline: {
        top: "-2px",
        border: "1px solid #ccc;",
      },
      input: {
        padding: "12px",
      },
      multiline: {
        padding: "12px",
      },
    },
    MuiFilledInput: {
      root: {
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
      },
    },
    MuiButton: {
      contained: {
        "&$disabled": {
          boxShadow: "none",
          backgroundColor: "white",
          border: "1px solid #abb0b8",
          padding: "5px 15px",
          borderRadius: "4px",
          color: "#abb0b8",
          opacity: 0.65,
        },
      },
    },
  },
};

export const groopTheme = createTheme(baseTheme);
export const groopInverseTheme = createTheme({
  ...baseTheme,
  overrides: {
    ...baseTheme.overrides,
    MuiButton: {
      containedPrimary: {
        backgroundColor: "#FFFFFE",
        color: "#06A1A8",
      },
    },
  },
});

export const groupDarkTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    primary: {
      main: groopTheme.palette.primary.dark,
    },
  },
});

export const aiTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    primary: {
      main: "#003E52",
      light: "#809FA9",
      dark: "#003041",
    },
  },
});

export type StyledAutoCompleteType = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;
export const StyledAutoComplete = withStyles((theme: Theme) => ({
  root: {
    "& [class^='MuiInputLabel-outlined'], & [class*=' MuiInputLabel-outlined']":
      {
        transform: "translate(14px, 21px) scale(1)",
      },
    "& [class^='MuiInputLabel-outlined'][class*=' MuiInputLabel-shrink'], & [class*=' MuiInputLabel-outlined'][class^='MuiInputLabel-shrink'], & [class*=' MuiInputLabel-outlined'][class*=' MuiInputLabel-shrink']":
      {
        transform: "translate(14px, -6px) scale(0.75)",
      },
    "& [class^='MuiOutlinedInput-root'], & [class*=' MuiOutlinedInput-root']": {
      paddingTop: theme.spacing(1.5),
    },
    "& [class^='MuiFilledInput-root'], & [class*=' MuiFilledInput-root']": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      borderRadius: "4px",
      "& [class^='MuiIconButton-root'], & [class*=' MuiIconButton-root']": {
        color: theme.palette.common.white,
      },
    },
    "& [class^='MuiInputLabel-filled'], & [class*=' MuiInputLabel-filled']": {
      color: theme.palette.common.white,
    },
  },
  option: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    '&[aria-selected="true"]': {
      backgroundColor: "transparent",
    },
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(Autocomplete) as StyledAutoCompleteType;

export const SubtleButton = withStyles((theme: Theme) => ({
  root: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[600],
    "&:hover": {
      backgroundColor: theme.palette.grey[700],
    },
    "&:active": {
      backgroundColor: theme.palette.grey[500],
    },
  },
}))(Button);

export const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.grey[300],
      width: "100%",
    },
    minWidth: "16%",
  },
}))(TableRow);

export const CompactRadioGroup = withStyles(() => ({
  root: {
    "& [class^='MuiFormControlLabel-root'], & [class*=' MuiFormControlLabel-root']":
      {
        margin: 0,
      },
  },
}))(RadioGroup);

export const CompactFormControlLabel = withStyles(() => ({
  root: {
    "& [class^='MuiFormControlLabel-label'], & [class*=' MuiformControlLabel-label']":
      {
        fontSize: "0.75rem",
      },
  },
}))(FormControlLabel);

export const ChipWithBlackText = withStyles((theme) => ({
  outlinedPrimary: {
    color: theme.palette.common.black,
  },
}))(Chip);
